import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Tooltip } from '@fluentui/react-components';
import { keyCode } from '@bingads-webui/keycode';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@adscore/with-responsive-size';

import { getAdPreviewPlaceholderStyles } from './styles';
import { getCustomizeAdPlacementList } from './customize-ad-preview-config';
import { getAdPlacementComponent } from './customize-ad-placements';

export const AdPreviewPlaceholder = ({ websiteInputRef, methodsContainerRef, logger }) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getAdPreviewPlaceholderStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const CustomizeAdPlacementList = getCustomizeAdPlacementList(i18n, displayMode);

  const onScrollToWebsiteInput = useCallback(() => {
    logger.trace('[AdPreviewPlaceholder] Ad preview clicked');
    if (websiteInputRef && websiteInputRef.current) {
      logger.trace('[AdPreviewPlaceholder] Scrolling to website input');
      websiteInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      websiteInputRef.current.focus();
    } else if (methodsContainerRef && methodsContainerRef.current) {
      logger.trace('[AdPreviewPlaceholder] Scrolling to campaign creation methods container');
      methodsContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [logger, methodsContainerRef, websiteInputRef]);

  const onKeyDown = useCallback((event) => {
    if (event.keyCode === keyCode.ENTER || event.keyCode === keyCode.SPACE) {
      onScrollToWebsiteInput();
    }
  }, [onScrollToWebsiteInput]);

  const onRenderPlacement = useCallback(
    (placementData, AdPlacementComponent) => {
      const { dataMapping, placementMeta } = placementData || {};
      const { placementType } = placementMeta || {};

      return (
        <Tooltip
          key={placementType}
          content={{
            children: i18n.getString(_TL_('Let\'s create ads for your business!')),
            className: classes.tooltip,
          }}
          relationship="label"
          positioning="above"
          withArrow
        >
          <div role="button" onClick={onScrollToWebsiteInput} onKeyDown={onKeyDown} tabIndex={0} className={classNames(classes.placementContainer, 'placement-container')}>
            <AdPlacementComponent
              placementData={placementData}
              i18n={i18n}
              {...dataMapping}
            />
          </div>
        </Tooltip>
      );
    },
    [classes.placementContainer, classes.tooltip, i18n, onKeyDown, onScrollToWebsiteInput]
  );

  const onRenderPlacementByIndex = useCallback((index) => {
    const placement = CustomizeAdPlacementList[index];
    const { placementMeta } = placement || {};
    const { placementType } = placementMeta || {};
    const AdPlacementComponent = getAdPlacementComponent(placementType);
    if (AdPlacementComponent) {
      return onRenderPlacement(placement, AdPlacementComponent);
    }
    return null;
  }, [CustomizeAdPlacementList, onRenderPlacement]);

  // when in mobile, we set column 1 &3 to display none, so no need to render column 1 & 3
  return (
    <div
      className={classNames(
        classes.adPreviewPlaceholderContainer,
        classes[`adPreviewPlaceholderContainer-${displayMode}`],
        'ad-preview-placeholder-container'
      )}
    >
      {
        displayMode !== 's' &&
        <div className={classNames(classes.adPreviewPlaceholderColumn1, 'ad-preview-placeholder-column1')}>
          {_.map([0, 1, 2], onRenderPlacementByIndex)}
        </div>
      }
      <div className={classNames(classes.adPreviewPlaceholderColumn2, 'ad-preview-placeholder-column2')}>
        {_.map([3, 4, 5, 6], onRenderPlacementByIndex)}
      </div>
      {
        displayMode !== 's' &&
        <div className={classNames(classes.adPreviewPlaceholderColumn3, 'ad-preview-placeholder-column3')}>
          {_.map([7, 8, 9], onRenderPlacementByIndex)}
        </div>
      }
    </div>
  );
};

AdPreviewPlaceholder.propTypes = {
  websiteInputRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  methodsContainerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};
AdPreviewPlaceholder.defaultProps = {
  websiteInputRef: null,
  methodsContainerRef: null,
  logger: {
    trace: _.noop,
  },
};
